import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router';
import { createRequestError, useComments } from './useComments';
import { useAnswers } from './AnswersProvider';
import { useReportFeedback } from 'donor/portal/pages/FeedbackDashboard/Feedback/useReportFeedback';
import { useCustomQuestions } from 'admin/pages/ManageAccounts/CustomQuestions/hook';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import {
  FeedbackCommentProps,
  FeedbackComments,
  FeedbackSection,
  FeedbackSectionId,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { ReportAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import { CustomQuestion } from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import { GuidanceTextResponse } from '../ReportBuilder/Components/utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FeedbackQuestion, UpdateAnswer } from '../NPOFeedback/MakeEdits/api';
import {
  requestError,
  requestInit,
  RequestState,
} from 'shared/auth/hooks/useRequestState';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import {
  AllVerificationsProgress,
  SelectedFeedback,
  VerificationProgress,
  feedbackSectionIdToString,
  getFeedbackList,
  getProgress,
} from '../NPOFeedback/utils';
import { SuccessResponse } from '../ReportsBeta/NewReport/api';
import { TrueImpactChild } from 'shared/design-system/components/TrueImpactBadge';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { AnswersFeedbacks, getAnswersFeedback } from '../NPOFeedback/api';
import { useClient } from 'shared/apollo';
import { FeedbackQuestionsById } from '../NPOFeedback/MakeEdits/api';
import {
  Answer,
  isCustomQuestion,
  makeCustomQuestionId,
  parseCustomQuestionId,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { getDatesPeriod } from '../ReportBuilder/Components/ReportFinalize/answersUtil';
import { toDigitDateString } from 'shared/utils/date';

const createQuestion = (
  customQuestions: CustomQuestion[],
  questionId: number,
): FeedbackQuestion & {
  sectionId: FeedbackSectionId;
} => {
  return {
    dbId: questionId,
    questionType: 'LONG_TEXT',
    sectionId: 'custom_questions',
    answers: [],
    enumResponses: [],
    text: '',
  };
};

export const isVerificationFeedback = (status: FeedbackStatus) => {
  return (
    status === 'Awaiting TI Verification' ||
    status === 'Awaiting Funder Verification'
  );
};

const isResolved = (
  status: FeedbackStatus,
  resolution: VerificationProgress,
): boolean => {
  if (
    (status === 'TI Feedback Provided' ||
      status === 'Funder Feedback Provided') &&
    resolution.resolved
  ) {
    return true;
  }
  if (
    status === 'Awaiting Funder Verification' &&
    resolution.donorVerified &&
    !resolution.donorUnverified
  ) {
    return true;
  }
  if (
    status === 'Awaiting TI Verification' &&
    resolution.tiVerified &&
    !resolution.tiUnverified
  ) {
    return true;
  }
  return false;
};

export interface ResolvedFeedback {
  questionIds: number[];
  questionsById: {
    [id: number]: boolean | undefined;
  };
}

export interface VerifyFeedback {
  questionIds: number[];
  questionsById: {
    [id: number]: 'verified' | 'rejected' | undefined;
  };
}

const updateResolutionState = (questionId: number) => (
  prev: ResolvedFeedback,
) => {
  const s = prev.questionsById[questionId];
  if (!s) return prev;
  return {
    questionIds: prev.questionIds.filter(id => id !== questionId),
    questionsById: {
      ...prev.questionsById,
      [questionId]: false,
    },
  };
};

export type HandleUpdateAnswer = Omit<UpdateAnswer, 'reportId'>;

const FeedbackContext = createContext<{
  userType: UserType;
  userStaticType: UserType;
  comments: FeedbackComments;
  answers: ReportAnswers;
  reportId: number;
  donorName: string;
  programName: string;
  reportStartDate: string;
  reportEndDate: string;
  email: string;
  name: string;
  isVerificationStatus: boolean;
  reportYear: string | undefined;
  feedbackStatus: FeedbackStatus;
  feedback: FeedbackDashboardCardProps;
  customQuestions: CustomQuestion[];
  guidanceText: GuidanceTextResponse;
  sections: FeedbackSection[];
  answersById: { [id: number]: Answer };
  feedbackByQuestions: SelectedFeedback[];
  verifyFeedback: VerifyFeedback;
  resolvedFeedback: ResolvedFeedback;
  questionFeedback: ResolvedFeedback;
  progress: AllVerificationsProgress;
  getLastResolvedAt: (questionId: number) => string | undefined;
  markAsResolvedRequestState: RequestState<unknown>;
  feedbackBySections: {
    [sectionId in FeedbackSectionId]: SelectedFeedback[];
  };
  updateAnswerRequest: RequestState<unknown>;
  commentRequestState: RequestState<
    | SuccessResponse<'newComment'>
    | SuccessResponse<'updateComment'>
    | SuccessResponse<'deleteComment'>
  >;
  setCustomQuestions: React.Dispatch<React.SetStateAction<CustomQuestion[]>>;
  markAsVerified: (
    questionId: number,
    isCustomQuestion: boolean,
    verified:
      | {
          verified: true;
        }
      | {
          verified: false;
          details?: string;
          text: string;
        },
  ) => Promise<unknown>;
  reloadAnswers: () => unknown;
  reloadFeedback: () => unknown;
  handleUnresolved: (questionId: number) => unknown;
  handleDoneQuestion: (
    questionId: number,
    selectedSection: SelectedFeedback,
    done: boolean,
    comment: string,
    details: string | undefined,
    index: number | undefined,
    isCustomQuestion: boolean,
  ) => unknown;
  updateAnswer: (a: HandleUpdateAnswer, s: FeedbackSectionId) => unknown;
  markAsDone: (
    selectedSection: SelectedFeedback,
    done: boolean,
    feedbackStatus: FeedbackStatus | undefined,
    isCustomQuestion: boolean,
  ) => unknown;
  deleteCommentState: (questionId: number, index: number) => unknown;
  setMarkAsResolvedRequest: React.Dispatch<
    React.SetStateAction<RequestState<SuccessResponse<'answersFeedback'>>>
  >;
  addOrUpdateCommentState: (
    questionId: number,
    comment: FeedbackCommentProps,
    index: number | undefined,
    isCustomQuestion: boolean,
  ) => unknown;
  addOrUpdateComment: (
    questionId: number,
    comment: FeedbackCommentProps,
    sectionId: FeedbackSectionId,
    details: string | undefined,
    index: number | undefined,
    isCustomQuestion: boolean,
  ) => unknown;
  deleteComment: (
    questionId: number,
    comment: FeedbackCommentProps,
    sectionId: FeedbackSectionId,
    index: number,
    isCustomQuestion: boolean,
  ) => unknown;
  feedbackQuestionsById: FeedbackQuestionsById;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
}>({} as any);

export const useFeedback = () => useContext(FeedbackContext);

export const FeedbackProvider: React.FC<{
  reportId: string | number;
  children: TrueImpactChild;
}> = ({ reportId, children }) => {
  const client = useClient();
  const { userId, userType, userStaticType, email, name } = useUserActions();
  reportId = reportId || useParams<{ feedbackId: string }>().feedbackId;
  const [progress, setProgress] = useState<AllVerificationsProgress>({});
  const [requestFeedbackState, makeFeedbackRequest] = useGraphQLRequest<
    AnswersFeedbacks
  >();
  const [resolvedFeedback, setResolvedFeedback] = useState<ResolvedFeedback>({
    questionIds: [],
    questionsById: {},
  });
  const [questionFeedback, setQuestionFeedback] = useState<ResolvedFeedback>({
    questionIds: [],
    questionsById: {},
  });
  const [verifyFeedback, setVerifyFeedback] = useState<VerifyFeedback>({
    questionIds: [],
    questionsById: {},
  });
  const {
    comments,
    sections,
    isCommentsLoading,
    feedbackBySections,
    commentQuestionIds,
    commentRequestState,
    commentsLoadingError,
    markAsResolvedRequestState,
    markAsDone,
    addOrUpdateComment,
    deleteComment,
    addOrUpdateCommentState,
    deleteCommentState,
    setMarkAsResolvedRequest,
  } = useComments(reportId, userType);
  const {
    answers,
    questions,
    answersById,
    isAnswersLoading,
    answersLoadingError,
    updateAnswer,
    reloadAnswers,
    updateAnswerRequest,
  } = useAnswers();
  const {
    feedback,
    isLoadingFeedback,
    feedbackLoadingError,
    reloadFeedback,
  } = useReportFeedback(reportId);
  const {
    setCustomQuestions,
    isLoading: isCustomQuestionLoading,
    customQuestionsWithoutGuidance: customQuestions,
    guidanceTextResponse: guidanceText,
  } = useCustomQuestions({ reportId: reportId });

  const markAsVerified = useCallback(
    async (
      questionId: number,
      isCustomQuestion: boolean,
      verified:
        | { verified: true }
        | {
            verified: false;
            details?: string;
            text: string;
          },
    ) => {
      setMarkAsResolvedRequest(requestInit());
      const question = isCustomQuestion
        ? createQuestion(customQuestions, questionId)
        : questions[questionId];
      if (!question || !feedback) {
        return setMarkAsResolvedRequest(
          requestError(createRequestError('Question not found')),
        );
      }
      const selectedSection = {
        question: {
          questionId,
          questionText: question.text,
        },
        section: {
          sectionId: question.sectionId,
          label: feedbackSectionIdToString(question.sectionId),
        },
      };
      if (!verified.verified) {
        const comment: FeedbackCommentProps = {
          name,
          email,
          commentId: 0,
          text: verified.text,
          accountType: userType,
          userId: Number(userId),
          details: verified.details,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        try {
          await addOrUpdateComment(
            questionId,
            comment,
            question.sectionId,
            verified.details,
            undefined,
            isCustomQuestion,
          );
        } catch (error) {
          return setMarkAsResolvedRequest(
            requestError(
              createRequestError('Error adding comment. Please try again.'),
            ),
          );
        }
      }
      return markAsDone(
        selectedSection,
        feedback.status,
        verified.verified,
        isCustomQuestion,
      )
        .then(() => {
          const id = isCustomQuestion
            ? Number(makeCustomQuestionId(questionId))
            : questionId;
          const status = verified.verified ? 'verified' : 'rejected';
          setVerifyFeedback(prev => ({
            ...prev,
            questionsById: {
              ...prev.questionsById,
              [id]: status,
            },
            questionIds: prev.questionIds.includes(id)
              ? prev.questionIds
              : [...prev.questionIds, id],
          }));
        })
        .catch(() => {
          return setMarkAsResolvedRequest(
            requestError(
              createRequestError(
                verified.verified
                  ? 'Error marking as verified. Please try again.'
                  : 'Error while requesting changes. Please try again.',
              ),
            ),
          );
        });
    },
    [
      questions,
      feedbackBySections,
      feedback?.status,
      markAsDone,
      reloadFeedback,
      reloadAnswers,
    ],
  );

  useEffect(() => {
    if (!feedback) return;
    const p = Object.keys(progress);
    if (p.length === 0) return;
    const status = feedback.status;
    const result: ResolvedFeedback = {
      questionIds: [],
      questionsById: {},
    };
    const verified: VerifyFeedback = {
      questionIds: [],
      questionsById: {},
    };
    for (const key of p) {
      const questionId = Number(key);
      const customQuestionId = isCustomQuestion(questionId)
        ? parseCustomQuestionId(questionId)
        : undefined;
      const resolution = progress[key];
      if (!commentQuestionIds[customQuestionId || questionId]) {
        continue;
      }
      if (isVerificationFeedback(status)) {
        const r =
          resolution[userType === 'ti staff' ? 'tiVerified' : 'donorVerified'];
        if (r) {
          verified.questionIds.push(questionId);
          verified.questionsById[questionId] = r ? 'verified' : 'rejected';
        }
      } else if (isResolved(status, resolution)) {
        result.questionIds.push(questionId);
        result.questionsById[questionId] = true;
      }
    }
    setResolvedFeedback(result);
    setVerifyFeedback(verified);
  }, [progress, feedback]);

  useEffect(() => {
    if (requestFeedbackState.type === 'REQUEST_INIT') {
      makeFeedbackRequest(getAnswersFeedback(client!, reportId));
    }
    if (requestFeedbackState.type === 'REQUEST_SUCCESS') {
      const p = getProgress(requestFeedbackState.data.allAnswersFeedback);
      setProgress(p);
    }
  }, [requestFeedbackState]);

  const handleUnresolved = useCallback(
    (questionId: number) => {
      setResolvedFeedback(updateResolutionState(questionId));
      setQuestionFeedback(updateResolutionState(questionId));
    },
    [setResolvedFeedback, setQuestionFeedback],
  );

  const getLastResolvedAt = useCallback(
    (questionId: number): string | undefined => {
      const p = progress[questionId];
      if (!p || !p.resolvedCount) return undefined;
      return p.lastResolved || p.resolved || p.updatedAt;
    },
    [progress],
  );

  const handleDone = useCallback(
    (
      selectedSection: SelectedFeedback,
      done: boolean,
      feedbackStatus: FeedbackStatus = feedback!.status,
      isCustomQuestion: boolean,
    ) => {
      markAsDone(selectedSection, feedbackStatus, done, isCustomQuestion).then(
        () => {
          const questionId = selectedSection.question?.questionId;
          if (!questionId) return;
          setResolvedFeedback(prev => ({
            questionIds: prev.questionIds.includes(questionId)
              ? prev.questionIds
              : [...prev.questionIds, questionId],
            questionsById: {
              ...prev.questionsById,
              [questionId]: done,
            },
          }));
          setQuestionFeedback(prev => ({
            questionIds: prev.questionIds.includes(questionId)
              ? prev.questionIds
              : [...prev.questionIds, questionId],
            questionsById: {
              ...prev.questionsById,
              [questionId]: !done,
            },
          }));
        },
      );
    },
    [markAsDone, feedback],
  );

  const handleDoneQuestion = useCallback(
    (
      questionId: number,
      selectedSection: SelectedFeedback,
      done: boolean,
      comment: string,
      details: string | undefined,
      index: number | undefined,
      isCustomQuestion: boolean,
    ) => {
      addOrUpdateComment(
        questionId,
        {
          userId: typeof userId === 'string' ? Number(userId) : userId,
          commentId: 0,
          text: comment,
          name: name,
          email: email,
          accountType: userType,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        selectedSection.section.sectionId,
        details,
        index,
        isCustomQuestion,
      ).then(() => {
        handleDone(selectedSection, done, feedback?.status, isCustomQuestion);
      });
    },
    [addOrUpdateComment, handleDone, name, email, userType],
  );

  const isLoading =
    isAnswersLoading ||
    isLoadingFeedback ||
    isCustomQuestionLoading ||
    requestFeedbackState.type === 'REQUEST_INIT' ||
    requestFeedbackState.type === 'REQUEST_START' ||
    isCommentsLoading;

  if (isLoading) {
    return <TrueImpactLoading />;
  }

  if (
    feedbackLoadingError ||
    !feedback ||
    answersLoadingError ||
    !sections ||
    !comments
  ) {
    return (
      <TrueImpactText color="critical">
        {feedbackLoadingError ||
          answersLoadingError ||
          commentsLoadingError ||
          'Unable to load feedback.'}
      </TrueImpactText>
    );
  }
  const period = getDatesPeriod(answers.dates);
  const f = getFeedbackList(sections, feedbackBySections, {
    question: undefined,
    section: sections[0],
  });
  return (
    <FeedbackContext.Provider
      value={{
        answers,
        comments,
        sections,
        feedback,
        email,
        name,
        userType,
        userStaticType: userStaticType || 'cn-npo',
        verifyFeedback,
        isVerificationStatus: isVerificationFeedback(feedback.status),
        setCustomQuestions,
        reloadFeedback,
        markAsVerified,
        setMarkAsResolvedRequest,
        reportId: feedback.id,
        feedbackQuestionsById: questions,
        feedbackStatus: feedback.status,
        reportStartDate:
          toDigitDateString(period.startDate) || feedback.startDate,
        reportEndDate: toDigitDateString(period.endDate) || feedback.endDate,
        reportYear: feedback.year,
        donorName: feedback.donorName,
        programName: feedback.programName,
        customQuestions,
        guidanceText,
        resolvedFeedback,
        questionFeedback,
        progress,
        getLastResolvedAt,
        updateAnswerRequest,
        markAsResolvedRequestState,
        answersById,
        updateAnswer,
        reloadAnswers,
        handleUnresolved,
        handleDoneQuestion,
        markAsDone: handleDone,
        addOrUpdateComment,
        deleteComment,
        addOrUpdateCommentState,
        deleteCommentState,
        commentRequestState,
        feedbackBySections: f.feedbackSections,
        feedbackByQuestions: f.feedbackByQuestions,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
};
